import { CloseFullscreenOutlined, PhotoCameraOutlined } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllPropPresets,
  selectSelectedElementType,
  selectSelectedTake3D,
  setSelectedInteraction,
} from "../../app/slices/TrainingSlice";
import CameraRenderPreview from "./CameraRenderPreview";
import TakeInspectThumbnail from "./TakeInspectThumbnail";
import { selectUsedProps } from "../../app/slices/PropLibrarySlice";
import {
  SelectedTab,
  selectSelectedTab,
} from "../../app/slices/GlobalSlice";

/** Same geometry as the picking card (Viewport3DCameraPicking), so the shot stays in one place. */
const NARROW_WIDTH = 900;
const CARD_MARGIN = 16;
const COLLAPSED_STORAGE_KEY = "cameraWidgetCollapsed";

/**
 * The take's starting view, docked top-right like the camera picking card. It is the
 * quiet idle version of that card: a caption says what the shot is, and clicking it
 * selects the take's starting view to change it. It collapses into a pill.
 */
export default function CameraWidget() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedTake = useSelector(selectSelectedTake3D);
  const isPropInspect = selectedTake?.startViewMode === "propInspect";
  const cameraId = isPropInspect ? "" : selectedTake?.mainCamera.cameraId;
  const props = useSelector(selectAllPropPresets);
  const usedProps = useSelector(selectUsedProps);
  const selection = selectedTake?.startPropInspect;
  const selectedProp = props.find((prop) => prop.id === selection?.propId);
  const inspectDefinition = selectedProp && selection?.tag
    ? usedProps[selectedProp.propId]?.metadata?.inspectDefinitions?.find((definition) => definition.name === selection.tag)
    : undefined;
  const selectedElementType = useSelector(selectSelectedElementType);

  const timelineSelected =
    useSelector(selectSelectedTab) === SelectedTab.Timeline;
  const hasStartingView = isPropInspect ? !!inspectDefinition : !!cameraId;
  const hasBlockingSelection =
    selectedElementType !== undefined &&
    selectedElementType !== "camera" &&
    selectedElementType !== "take";
  // Picking the take's camera shows the shot docked in the map (Viewport3DCameraPicking).
  const isCameraPicking = selectedElementType === "camera" && !isPropInspect;
  const isVisible =
    !hasBlockingSelection &&
    !isCameraPicking &&
    selectedTake !== undefined &&
    timelineSelected &&
    hasStartingView;

  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true"
  );
  const setCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const parent = rootRef.current?.parentElement;
    if (!parent || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() =>
      setIsNarrow(parent.clientWidth < NARROW_WIDTH)
    );
    observer.observe(parent);
    return () => observer.disconnect();
  }, [isVisible]);

  if (!isVisible || !selectedTake) return null;

  const shotName =
    isPropInspect && selectedProp && inspectDefinition
      ? `${selectedProp.name} · ${inspectDefinition.name.replace(/_/g, " ")}`
      : selectedTake.mainCamera.name;
  const startingView = String(t("take_starting_view", "Starting view"));
  const selectStartingView = () =>
    dispatch(setSelectedInteraction(selectedTake.mainCamera.id));

  if (isCollapsed) {
    return (
      <div ref={rootRef} className="absolute z-10" style={{ top: CARD_MARGIN, right: CARD_MARGIN }}>
        <button
          type="button"
          aria-label="camera-widget"
          title={String(t("camera_widget_expand", "Show the starting view"))}
          className="flex items-center gap-[6px] max-w-[240px] h-[32px] px-[10px] rounded-full border border-solid border-creator-text-faded bg-creator-surface text-[12px] text-creator-text-sub shadow-[0_8px_20px_rgba(16,24,40,0.10)] cursor-pointer hover:bg-creator-surfaceHover"
          onClick={() => setCollapsed(false)}
        >
          <PhotoCameraOutlined sx={{ fontSize: 16 }} className="text-creator-primary" />
          <span className="truncate">{shotName}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      aria-label="camera-widget"
      className="group absolute z-10 p-[6px] box-border rounded-[8px] border border-solid border-creator-text-faded bg-creator-surface shadow-[0_16px_40px_rgba(16,24,40,0.12)]"
      style={{ top: CARD_MARGIN, right: CARD_MARGIN, width: isNarrow ? 240 : 340 }}
    >
      <button
        type="button"
        className="relative block w-full p-0 border-0 bg-transparent cursor-pointer text-left"
        title={String(t("camera_widget_change", "Change the starting view"))}
        onClick={selectStartingView}
      >
        {isPropInspect && selectedProp && inspectDefinition ? (
          <div
            className="aspect-video flex items-center justify-center overflow-hidden rounded-[6px] bg-creator-surfaceHover"
            data-testid="camera-widget-frame"
          >
            <TakeInspectThumbnail
              key={`${selectedProp.id}/${inspectDefinition.name}/${inspectDefinition.id ?? ""}`}
              prop={selectedProp}
              tag={inspectDefinition.name}
              renderId={inspectDefinition.id}
            />
          </div>
        ) : (
          <CameraRenderPreview cameraId={cameraId ?? ""} />
        )}
        <div className="absolute inset-x-0 bottom-0 rounded-b-[6px] px-[10px] pt-[20px] pb-[6px] bg-gradient-to-t from-black/60 to-transparent text-[12px] text-white truncate select-none pointer-events-none">
          {startingView} · {shotName}
        </div>
      </button>
      <button
        type="button"
        aria-label={String(t("camera_widget_collapse", "Hide the starting view"))}
        title={String(t("camera_widget_collapse", "Hide the starting view"))}
        className="absolute top-[12px] right-[12px] flex items-center justify-center w-[24px] h-[24px] p-0 rounded-full border-0 bg-black/50 text-white cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
        onClick={() => setCollapsed(true)}
      >
        <CloseFullscreenOutlined sx={{ fontSize: 14 }} />
      </button>
    </div>
  );
}
